import { z } from 'zod';
import { BUSINESS_INTEREST_LABELS } from './business-interests';

/**
 * Канонический каталог интересов профиля знакомств.
 *
 * Источник истины — легаси `profile.html` (`<select id="interest1">`, 38 пунктов
 * + пустой placeholder). Порядок сохранён для UI. `slug` — стабильный код в БД,
 * `label` — русское отображение. Список фиксированный (не админ-каталог): меняется
 * только правкой кода, поэтому живёт в contracts, а не в Prisma-модели.
 */
export const INTEREST_SLUGS = [
  'travel',
  'hiking',
  'movies',
  'dining',
  'psychology',
  'board-games',
  'coffee',
  'fitness',
  'deep-talks',
  'yoga',
  'dancing',
  'running-cycling',
  'live-music',
  'workshops',
  'personal-growth',
  'photo-walks',
  'meditation',
  'networking',
  'tea-ceremonies',
  'languages',
  'parties-karaoke',
  'volunteering',
  'wine-cocktails',
  'surfing',
  'transformational-games',
  'spa',
  'co-creativity',
  'biohacking',
  'breathwork',
  'retreats',
  'philosophy-books',
  'moto-auto',
  'spiritual-practices',
  'energy-practices',
  'astrology',
  'billiards',
  'picnics',
  'shopping',
] as const;

export const InterestSlug = z.enum(INTEREST_SLUGS);
export type InterestSlug = z.infer<typeof InterestSlug>;

/** Русские названия интересов по slug. */
export const INTEREST_LABELS: Record<InterestSlug, string> = {
  travel: 'Путешествия',
  hiking: 'Прогулки и хайкинг',
  movies: 'Кинопросмотры и обсуждения',
  dining: 'Рестораны и гастрономия',
  psychology: 'Психология и саморефлексия',
  'board-games': 'Настольные игры и квесты',
  coffee: 'Кофе и завтраки',
  fitness: 'Фитнес и спортзал',
  'deep-talks': 'Глубокие разговоры по душам',
  yoga: 'Йога и растяжка',
  dancing: 'Танцы',
  'running-cycling': 'Бег и велопрогулки',
  'live-music': 'Концерты и живая музыка',
  workshops: 'Мастер-классы и воркшопы',
  'personal-growth': 'Личностный рост и коучинг',
  'photo-walks': 'Фотопрогулки',
  meditation: 'Медитация и осознанность',
  networking: 'Нетворкинг и бизнес-сообщества',
  'tea-ceremonies': 'Чайные церемонии',
  languages: 'Изучение иностранных языков',
  'parties-karaoke': 'Вечеринки и караоке',
  volunteering: 'Волонтерство и благотворительность',
  'wine-cocktails': 'Винные дегустации и коктейли',
  surfing: 'Серфинг и сапбординг',
  'transformational-games': 'Трансформационные игры',
  spa: 'Баня, спа и массаж',
  'co-creativity': 'Совместное творчество',
  biohacking: 'Биохакинг и здоровье',
  breathwork: 'Дыхательные и телесные практики',
  retreats: 'Ретриты и загородный отдых',
  'philosophy-books': 'Философия и чтение книг',
  'moto-auto': 'Мото-прогулки и автопутешествия',
  'spiritual-practices': 'Духовные практики и цигун',
  'energy-practices': 'Энергетические практики',
  astrology: 'Астрология и дизайн человека',
  billiards: 'Бильярд',
  picnics: 'Пикники',
  shopping: 'Шопинг',
};

/** Потолок деловых сфер и фейковых анкет (легаси: 3 `<select>`). */
export const MAX_INTERESTS = 3;

/** Личные интересы: каталог плюс свои теги. Порядок массива — важность. */
export const MAX_PERSONAL_INTERESTS = 10;

/** Каталог в виде массива `{ slug, label }` в каноническом порядке — для пикеров. */
export const INTEREST_CATALOG: ReadonlyArray<{ slug: InterestSlug; label: string }> = INTEREST_SLUGS.map(
  (slug) => ({ slug, label: INTEREST_LABELS[slug] }),
);

/**
 * Безопасный резолв label по slug (неизвестный slug отдаём как есть).
 *
 * Ищет в ОБОИХ словарях — личных интересов и деловых сфер. Благодаря этому
 * `@upme/view-logic` (поиск по каталогу «Люди», подписи общих интересов) про режимы не
 * знает вовсе: один резолвер обслуживает оба. Условие корректности — слаги словарей не
 * пересекаются; закреплено спекой `interests.spec.ts`.
 */
export function interestLabel(slug: string): string {
  return (
    (INTEREST_LABELS as Record<string, string>)[slug] ??
    (BUSINESS_INTEREST_LABELS as Record<string, string>)[slug] ??
    slug
  );
}

const INTEREST_SUGGESTIONS_MAX = 8;

/**
 * Подсказки личных интересов: живые теги анкет и совпадения каталога со счётчиком 0.
 * Игла ищется и в слаге, и в русской подписи.
 */
export function interestSuggestions(
  query: string,
  used: readonly { tag: string; count: number }[],
): Array<{ tag: string; count: number }> {
  const needle = query.trim().replace(/^#+/u, '').trim().toLowerCase();
  if (!needle) return [];
  const counts = new Map<string, number>();
  for (const row of used) {
    const tag = row.tag.trim();
    if (!tag) continue;
    const label = interestLabel(tag).toLowerCase();
    if (!tag.toLowerCase().includes(needle) && !label.includes(needle)) continue;
    counts.set(tag, Math.max(counts.get(tag) ?? 0, row.count));
  }
  for (const item of INTEREST_CATALOG) {
    const hit = item.slug.includes(needle) || item.label.toLowerCase().includes(needle);
    if (hit && !counts.has(item.slug)) counts.set(item.slug, 0);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || interestLabel(a.tag).localeCompare(interestLabel(b.tag), 'ru'))
    .slice(0, INTEREST_SUGGESTIONS_MAX);
}

export const InterestTagSuggestionQuery = z.object({
  q: z.string().trim().min(1).max(80),
});
export type InterestTagSuggestionQuery = z.infer<typeof InterestTagSuggestionQuery>;
